<template>
  <div class="relative left-1/2 right-1/2 -mx-[50vw] w-screen">
    <div class="mx-auto w-full max-w-[1840px] px-10">
      <section class="mt-10">
        <div class="w-full">
          <div class="mb-6">
            <h1 class="text-3xl font-semibold text-gray-900">
              Edit Page
            </h1>
            <p class="text-sm text-gray-600 mt-1">
              Update the website page content.
            </p>
          </div>
          <div
            v-if="loading"
            class="text-sm text-gray-500"
          >
            Loading page...
          </div>
          <div
            v-else-if="error"
            class="text-sm text-red-600"
          >
            {{ error }}
          </div>
          <div v-else>
            <form @submit.prevent="updatePage">
              <div class="grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
                <div>
                  <PageBuilder
                    v-model="form.contentJson"
                    :selected-id="selectedBlockId"
                    @select="selectedBlockId = $event"
                  />
                </div>

                <aside class="space-y-6">
                  <BlockEditor :block="selectedBlock" />
                  <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                    <h3 class="text-sm font-semibold text-gray-900">
                      Page Info
                    </h3>
                    <div class="mt-3 space-y-4">
                      <div>
                        <label class="block mb-2 text-xs font-medium text-gray-600">Title</label>
                        <input
                          v-model="form.title"
                          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                          required
                          type="text"
                        >
                      </div>
                      <div>
                        <label class="block mb-2 text-xs font-medium text-gray-600">Slug</label>
                        <input
                          v-model="form.slug"
                          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                          required
                          type="text"
                        >
                      </div>
                      <div>
                        <label class="block mb-2 text-xs font-medium text-gray-600">Summary</label>
                        <textarea
                          v-model="form.summary"
                          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>
                  <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                    <h3 class="text-sm font-semibold text-gray-900">
                      Publishing
                    </h3>
                    <div class="mt-3 space-y-4">
                      <div>
                        <label class="block mb-2 text-xs font-medium text-gray-600">Status</label>
                        <select
                          v-model="form.status"
                          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                        >
                          <option value="published">
                            Published
                          </option>
                          <option value="draft">
                            Draft
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div class="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
                    <h3 class="text-sm font-semibold text-gray-900">
                      SEO
                    </h3>
                    <div class="mt-3 space-y-4">
                      <div>
                        <label class="block mb-2 text-xs font-medium text-gray-600">SEO Title</label>
                        <input
                          v-model="form.seoTitle"
                          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                          type="text"
                        >
                      </div>
                      <div>
                        <label class="block mb-2 text-xs font-medium text-gray-600">SEO Description</label>
                        <textarea
                          v-model="form.seoDescription"
                          class="block w-full rounded-lg border border-gray-300 bg-white p-2.5 text-sm text-gray-900 focus:border-primary focus:ring-primary"
                          rows="3"
                        />
                      </div>
                    </div>
                  </div>
                </aside>
              </div>
              <div class="mt-6 flex items-center gap-3">
                <button
                  class="inline-flex items-center rounded-lg bg-black px-5 py-2.5 text-sm font-semibold text-white hover:bg-gray-800"
                  type="submit"
                >
                  Save Changes
                </button>
                <router-link
                  class="text-sm font-medium text-gray-600 hover:text-gray-900"
                  to="/administrator/pages"
                >
                  Cancel
                </router-link>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, onMounted, ref, watch, watchEffect} from 'vue';
import axios from 'axios';
import {Page} from '@/types';
import {useHead} from "@vueuse/head";
import PageBuilder from '@/components/PageBuilder.vue';
import BlockEditor from '@/components/BlockEditor.vue';

export default defineComponent({
  name: 'PageEdit',
  components: {PageBuilder, BlockEditor},
  props: {
    id: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const loading = ref(false);
    const error = ref('');
    const selectedBlockId = ref('');
    const defaultContent = {version: 1, blocks: [] as any[]};
    const form = ref({
      title: '',
      slug: '',
      summary: '',
      status: 'published',
      seoTitle: '',
      seoDescription: '',
      contentJson: defaultContent,
    });

    const fetchPage = async (): Promise<void> => {
      loading.value = true;
      try {
        const response = await axios.get<Page>(`/pages/${props.id}`);
        const page = response.data;
        form.value = {
          title: page.title,
          slug: page.slug,
          summary: page.summary || '',
          status: page.status || 'published',
          seoTitle: page.seoTitle || '',
          seoDescription: page.seoDescription || '',
          contentJson: (page.contentJson ?? defaultContent) as any,
        };
      } catch (err) {
        console.error(err);
        error.value = 'Failed to load page.';
      } finally {
        loading.value = false;
      }
    };

    const updatePage = async (): Promise<void> => {
      error.value = '';
      try {
        await axios.put(`/pages/${props.id}`, form.value);
      } catch (err) {
        console.error(err);
        error.value = 'Failed to update page.';
      }
    };

    const findBlockById = (blocks: any[], id: string): any | null => {
      for (const block of blocks) {
        if (block.id === id) return block;
        if (block.type === 'columns' && Array.isArray(block.data?.items)) {
          for (const column of block.data.items) {
            const found = findBlockById(column || [], id);
            if (found) return found;
          }
        }
      }
      return null;
    };

    const selectedBlock = computed(() => {
      const blocks = (form.value.contentJson?.blocks || []) as any[];
      return findBlockById(blocks, selectedBlockId.value) || null;
    });

    watch(
      () => form.value.contentJson,
      () => {
        const blocks = (form.value.contentJson?.blocks || []) as any[];
        if (!findBlockById(blocks, selectedBlockId.value)) {
          selectedBlockId.value = '';
        }
      },
      {deep: true}
    );

    onMounted(fetchPage);

    watchEffect(() => {
      useHead({
        title: 'Edit Page',
      });
    });

    return {
      loading,
      error,
      form,
      updatePage,
      selectedBlockId,
      selectedBlock,
    };
  },
});
</script>
